/**
 * Built-in trait definitions for the proof. grammar.
 *
 * A trait is a named capability flag that a section block may carry.
 * Traits drive rendering treatment (§5.6), aggregation eligibility,
 * and observation-log behaviour.
 *
 * This module declares the seed trait set for the base grammar.
 * Track 2.A will extend this with user-declarable traits via `.cookrc.yml`.
 *
 * Pure TS — no DOM, no Node built-ins.
 */

// ---------------------------------------------------------------------------
// Trait names (canonical — English in the source, per spec §5.2)
// ---------------------------------------------------------------------------

export type TraitName =
  | "scalable"
  | "timed"
  | "unit-aware"
  | "observable"
  | "charted"
  | "rating"
  | "shopping-list"
  | "fed"
  | "consumable"
  | "metadata";

// ---------------------------------------------------------------------------
// Trait definitions
// ---------------------------------------------------------------------------

export interface TraitDefinition {
  name: TraitName;
  description: string;
}

export const TRAITS: readonly TraitDefinition[] = [
  {
    name: "scalable",
    description: "Values in this block scale with @scale directives.",
  },
  {
    name: "timed",
    description: "Block contains duration values; renders with clock icon.",
  },
  {
    name: "unit-aware",
    description: "Values carry explicit units; supports unit conversion display.",
  },
  {
    name: "observable",
    description: "Block contributes to the observation timeline.",
  },
  {
    name: "charted",
    description: "Values contribute to per-recipe history charts (wishlisted for MVP).",
  },
  {
    name: "rating",
    description: "Block is a rating block; renders with star/boolean/enum widgets.",
  },
  {
    name: "shopping-list",
    description: "Ingredients in this block appear in the shopping list view.",
  },
  {
    name: "fed",
    description: "Artefact kind trait: the artefact is regularly fed (starter).",
  },
  {
    name: "consumable",
    description: "Artefact kind trait: the artefact is consumed across attempts.",
  },
  {
    name: "metadata",
    description: "Block carries metadata (tags, notes) rather than recipe variables.",
  },
] as const;

// ---------------------------------------------------------------------------
// Section keyword → default trait set
// ---------------------------------------------------------------------------

/**
 * Default traits for built-in section keywords.
 *
 * These map the canonical seed sections to their declared traits per
 * `extensibility-design.md §12` and `cook-as-process-design.md §6.4`.
 */
export const SECTION_DEFAULT_TRAITS: ReadonlyMap<string, readonly TraitName[]> = new Map([
  ["ingredients", ["scalable", "unit-aware", "shopping-list"]],
  ["ferment", ["timed", "unit-aware", "observable"]],
  ["oven", ["timed", "unit-aware"]],
  ["rating", ["rating"]],
  ["tags", ["metadata"]],
  ["notes", ["metadata"]],
  ["observe", ["observable", "timed"]],
  ["method", ["metadata"]],
  ["scale", ["scalable"]],
]);

// ---------------------------------------------------------------------------
// Trait lookup
// ---------------------------------------------------------------------------

/**
 * Return the default trait names for a given section keyword.
 * Returns an empty array for unknown/custom keywords.
 */
export function defaultTraitsFor(keyword: string): readonly TraitName[] {
  return SECTION_DEFAULT_TRAITS.get(keyword) ?? [];
}

/**
 * Check whether a section keyword has a specific trait.
 */
export function hasDefaultTrait(keyword: string, trait: TraitName): boolean {
  return defaultTraitsFor(keyword).includes(trait);
}
